import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { theme } from '_constants/theme';
import { ICONS_ALIASES } from '_constants/icons';

import Button from '_components/atomic/Button';
import Icon from '_components/atomic/Icon';
import MainBannerSection from '../MainBannerSection';
import Bullet from '_components/atomic/Bullet';
import DescriptionButton from '_components/atomic/DescriptionButton';
import Router, { useRouter } from 'next/router';
import React, { useState } from 'react';
import getConfig from 'next/config';
import Input from '_components/atomic/Input';
import styled from 'styled-components';
import { ROUTES } from '_constants/routes';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { config } from '_config';

const { publicRuntimeConfig } = getConfig();
const { colors, devices, fontSizes, spaces } = theme;

const StyledHomeBannerSection = styled.div`
  width: 100%;

  .bottomSearchContainer {
    width: 100%;
  }
  .bottomSearchContainer > div:first-child {
    margin-bottom: ${spaces.large2X - 2}px;
  }
  @media ${devices.tablet} {
    .bottomSearchContainer > div:first-child {
      margin-bottom: ${spaces.regular}px;
    }
  }
`;
const StyledRecentlyTrademarked = styled.div`
  margin-top: 50px;
  display: flex;
  flex-direction: row;
  @media ${devices.tablet} {
    flex-direction: column;
    width: 100%;
  }
`;
const StyledRecentlyTrademarkedHeader = styled.div`
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 400;
  font-size: ${fontSizes.large}px;
  line-height: 150%;
  color: ${colors.gray[800]};
  white-space: nowrap;
  margin-right: 82px;
  display: flex;
  align-items: center;
  @media ${devices.tablet} {
    font-size: ${fontSizes.regular}px;
    margin-right: 0px;
    margin-bottom: ${spaces.regular}px;
    justify-content: center;
  }
`;
const StyledRecentlyTrademarkedSwiper = styled.div`
  display: contents;
  .swiper {
    margin-left: 40px;
    margin-right: 40px;
    display: flex;
    flex-direction: column;
  }
  .swiper-slide {
    width: 17.3%;
    display: flex;
    align-items: center;
  }
  @media ${devices.tablet} {
    .swiper-slide {
      width: 42%;
    }
  }
`;
const StyledRecentlyTrademarkedSwiperContainer = styled.div`
  display: flex;
  .navButton {
    cursor: pointer;
  }
  .swiper-button-disabled {
    cursor: initial;
    svg {
      path {
        fill: ${colors.gray[500]};
      }
    }
  }
`;
const StyledRecentlyTrademarkedSwiperNav = styled.div`
  display: flex;
  align-items: center;
`;
const StyledLogoCard = styled.div`
  .logoImg {
    width: 100%;
    max-height: 50px;
  }
`;
export const logoSwiper = [
  { imgSrc: `${config.basePath}/images/logoswiper1.svg` },
  { imgSrc: `${config.basePath}/images/logoswiper2.svg` },
  { imgSrc: `${config.basePath}/images/logoswiper3.svg` },
  { imgSrc: `${config.basePath}/images/logoswiper4.svg` },
  { imgSrc: `${config.basePath}/images/logoswiper5.svg` },
];
const LogoSwiper = () => {
  const swiperRef = React.useRef(null);
  return (
    <StyledRecentlyTrademarked>
      <StyledRecentlyTrademarkedHeader>
        Recently Trademarked:
      </StyledRecentlyTrademarkedHeader>
      <StyledRecentlyTrademarkedSwiperContainer>
        <StyledRecentlyTrademarkedSwiperNav
          className="navButton"
          onClick={() => swiperRef.current.swiper.slidePrev()}
        >
          <Icon
            alias={ICONS_ALIASES.arrowSquareLeft}
            color={colors.gray[800]}
          />
        </StyledRecentlyTrademarkedSwiperNav>
        <StyledRecentlyTrademarkedSwiper>
          <Swiper
            loop={true}
            spaceBetween={32}
            slidesPerView="auto"
            ref={swiperRef}
          >
            {logoSwiper.map((logo, idx) => {
              const { imgSrc } = logo;
              return (
                <SwiperSlide key={`step:${idx}`}>
                  <StyledLogoCard>
                    <picture>
                      <source srcSet={imgSrc} type="image/png" />
                      <img className="logoImg" src={imgSrc} alt="" />
                    </picture>
                  </StyledLogoCard>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </StyledRecentlyTrademarkedSwiper>
        <StyledRecentlyTrademarkedSwiperNav
          className="navButton"
          onClick={() => swiperRef.current.swiper.slideNext()}
        >
          <Icon
            alias={ICONS_ALIASES.arrowSquareRight}
            color={colors.gray[800]}
          />
        </StyledRecentlyTrademarkedSwiperNav>
      </StyledRecentlyTrademarkedSwiperContainer>
    </StyledRecentlyTrademarked>
  );
};
const HomeBannerSection = ({ search, setSearch, setPage }) => {
  const router = useRouter();

  const handleClick = useCallback(
    (event) => {
      event.preventDefault();
      setPage('1');
      router.push({
        pathname: ROUTES.search,
        query: { text: search },
      });
    },
    [router, search, setPage]
  );

  return (
    <StyledHomeBannerSection>
      <MainBannerSection
        imgSrc={`${publicRuntimeConfig.basePath}/images/home.svg`}
        recentlyTrademarked={<LogoSwiper />}
        hideImageMobile
        topSection={
          <div>
            <Bullet
              icon={
                <Icon
                  alias={ICONS_ALIASES.upload}
                  color={theme.colors.blue[500]}
                />
              }
              text="File Your Trademark Online"
            />
            <Bullet
              icon={
                <Icon
                  alias={ICONS_ALIASES.lock}
                  color={theme.colors.blue[500]}
                />
              }
              text="Official USPTO Registration process"
            />
            <Bullet
              icon={
                <Icon
                  alias={ICONS_ALIASES.shieldTick}
                  color={theme.colors.blue[500]}
                />
              }
              text="Protect Your Brand Today"
            />
          </div>
        }
        bottomSection={
          <div className="bottomSearchContainer">
            <Input
              borderColor={theme.colors.gray[200]}
              textColor={theme.colors.gray[500]}
              color={theme.colors.white}
              hoverColor={theme.colors.white}
              hoverTextColor={theme.colors.gray[500]}
              placeholder="Search trademark availability"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(event) => {
                if (event.key === 'Enter') {
                  handleClick(event);
                }
              }}
              suffix={
                <Button onClick={handleClick} color={theme.colors.orange[500]}>
                  Search
                </Button>
              }
            />
            <DescriptionButton
              suffix={
                <Button
                  onClick={() => Router.push(ROUTES.trademarkRegistration)}
                  suffix={
                    <Icon
                      alias={ICONS_ALIASES.arrowRight}
                      color={theme.colors.orange[500]}
                    />
                  }
                  textColor={theme.colors.orange[500]}
                  color={theme.colors.orange[25]}
                  hoverColor={theme.colors.white}
                  hoverTextColor={theme.colors.orange[500]}
                  large
                >
                  Start Registering Now
                </Button>
              }
              borderColor={theme.colors.orange[500]}
              textColor={theme.colors.white}
              color={theme.colors.orange[500]}
              mobileColumnMode
              large
            >
              Already know your Trademark is available?
            </DescriptionButton>
          </div>
        }
      />
    </StyledHomeBannerSection>
  );
};

HomeBannerSection.propTypes = {
  search: PropTypes.string,
  setSearch: PropTypes.func,
  setPage: PropTypes.func,
};

export default HomeBannerSection;
